import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { AudioProvider } from "./context/AudioContext";
import { PreferencesProvider } from "./context/PreferencesContext";
import { AssignmentBadgesProvider } from "./context/AssignmentBadgesContext";
import { Layout } from "./components/Layout";
import { MiniPlayer } from "./components/audio/MiniPlayer";
import { AudioDownloadPrompt } from "./components/audio/AudioDownloadPrompt";
import Splashscreen from "./components/Splashscreen";
import { requestPersistentStorage } from "./audio/persistStorage";
import { notificationService } from "./services/notificationService";

// Pages
import Landing from "./pages/Landing";
import QuranList from "./pages/QuranList";
import SurahDetail from "./pages/SurahDetail";
import ProphetTimeline from "./pages/ProphetTimeline";
import EventDetail from "./pages/EventDetail";
import Companions from "./pages/Companions";
import CompanionDetail from "./pages/CompanionDetail";
import Prophets from "./pages/Prophets";
import ProphetDetail from "./pages/ProphetDetail";
import SearchPage from "./pages/SearchPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import AdminProphets from "./pages/AdminProphets";
import PrayerTimes from "./pages/PrayerTimes";
import QiblaCompass from "./pages/QiblaCompass";
import Donations from "./pages/Donations";
import QuestDetail from "./pages/QuestDetail";
import AdminQuests from "./pages/AdminQuests";
import Bookmarks from "./pages/Bookmarks";
import SettingsPage from "./pages/SettingsPage";
import HadithCollections from "./pages/HadithCollections";
import HadithBooks from "./pages/HadithBooks";
import HadithEntries from "./pages/HadithEntries";
import HadithDetail from "./pages/HadithDetail";
import AdminLinks from "./pages/AdminLinks";
import AudioDownloads from "./pages/AudioDownloads";
import Memorization from "./pages/Memorization";
import MemorizationCreate from "./pages/MemorizationCreate";
import MemorizationSession from "./pages/MemorizationSession";
import DisplaySettings from "./pages/DisplaySettings";
import { LemmaDetail, RootDetail, LexicalSearchPage } from "./pages/LexicalDetail";
import { TajwidIndex, TajwidLesson } from "./pages/Tajwid";
import { TajwidLeaderboard } from "./pages/TajwidLeaderboard";
import { CoursIndex } from "./pages/Cours";
import { ApprendreALire } from "./pages/ApprendreALire";
import { LectureAudioUpload } from "./pages/LectureAudioUpload";
import { MesProgres } from "./pages/MesProgres";
import { Devoirs } from "./pages/Devoirs";
import { MadrassaPage } from "./pages/Madrassa";
import { MadrassaAdminPage } from "./pages/MadrassaAdmin";
import { AdminMadrassasPage } from "./pages/AdminMadrassas";
import { AdminMadrassaDetailPage } from "./pages/AdminMadrassaDetail";
import { AdminMadrassaRequestsPage } from "./pages/AdminMadrassaRequests";
import { MadrassaTeacherInvitationsPage } from "./pages/MadrassaTeacherInvitations";
import ParentDashboardPage from "./pages/ParentDashboard";
import ParentLinkConfirm from "./pages/ParentLinkConfirm";
import TeacherRecitationsReviewPage from "./pages/TeacherRecitationsReview";
import { AdminUsersPage, AdminUserDetailPage } from "./pages/AdminUsers";
import { TeacherClassesPage, TeacherClassDetailPage } from "./pages/TeacherClasses";
import { CompanionsStories } from "./pages/CompanionsStories";

function App() {
  // Splash coordination: keep content invisible until splash finishes.
  // Checked synchronously so first render already has the right value.
  const [contentReady] = useState(() => {
    try { return sessionStorage.getItem('noor_splash_shown_v1') === '1'; }
    catch { return true; }
  });
  const [contentVisible, setContentVisible] = useState(contentReady);

  useEffect(() => { requestPersistentStorage(); }, []);

  // Initialize notification service AFTER first render (non-blocking)
  useEffect(() => {
    const timer = setTimeout(() => {
      notificationService.initialize().catch((e) => {
        console.warn('Notification initialization failed:', e);
      });
    }, 2000); // 2s delay — let the app render first
    return () => clearTimeout(timer);
  }, []);

  return (
    <AuthProvider>
        <PreferencesProvider>
          <AudioProvider>
            <AssignmentBadgesProvider>
            <BrowserRouter>
              <Splashscreen onDone={() => setContentVisible(true)} />
              <div
                style={{
                  opacity: contentVisible ? 1 : 0,
                  transition: contentVisible ? 'opacity 0.35s ease' : 'none',
                  pointerEvents: contentVisible ? 'auto' : 'none',
                }}
              >
              <Layout>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/quran" element={<QuranList />} />
              <Route path="/coran" element={<Navigate to="/quran" replace />} />
              <Route path="/coran/lire" element={<Navigate to="/quran" replace />} />
              <Route path="/coran/memoriser" element={<Navigate to="/memorization" replace />} />
              <Route path="/quran/:surahNumber" element={<SurahDetail />} />
              <Route path="/prophet-timeline" element={<ProphetTimeline />} />
              <Route path="/events/:eventId" element={<EventDetail />} />
              <Route path="/companions" element={<Companions />} />
              <Route path="/companions/stories" element={<CompanionsStories />} />
              <Route path="/compagnons" element={<Navigate to="/companions" replace />} />
              <Route path="/compagnons/liste" element={<Navigate to="/companions" replace />} />
              <Route path="/compagnons/histoires" element={<Navigate to="/companions/stories" replace />} />
              <Route path="/companions/:companionId" element={<CompanionDetail />} />
              <Route path="/prophets" element={<Prophets />} />
              <Route path="/prophets/:prophetId" element={<ProphetDetail />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/prophets" element={<AdminProphets />} />
              <Route path="/prieres" element={<PrayerTimes />} />
              <Route path="/prieres/horaires" element={<PrayerTimes />} />
              <Route path="/prieres/qibla" element={<QiblaCompass />} />
              <Route path="/admin/quests" element={<AdminQuests />} />
              <Route path="/dons" element={<Donations />} />
              <Route path="/dons/quete/:slug" element={<QuestDetail />} />
              <Route path="/bookmarks" element={<Bookmarks />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/audio" element={<AudioDownloads />} />
              <Route path="/hadith" element={<HadithCollections />} />
              <Route path="/hadith/entry/:entryId" element={<HadithDetail />} />
              <Route path="/hadith/:slug" element={<HadithBooks />} />
              <Route path="/hadith/:slug/book/:bookNumber" element={<HadithEntries />} />
              <Route path="/admin/links" element={<AdminLinks />} />
              <Route path="/memorization" element={<Memorization />} />
              <Route path="/memorization/new" element={<MemorizationCreate />} />
              <Route path="/memorization/:planId" element={<MemorizationSession />} />
              <Route path="/settings/display" element={<DisplaySettings />} />
              <Route path="/lexical/search" element={<LexicalSearchPage />} />
              <Route path="/lexical/lemma/:lemma" element={<LemmaDetail />} />
              <Route path="/lexical/root/:root" element={<RootDetail />} />
              <Route path="/cours" element={<CoursIndex />} />
              <Route path="/mes-progres" element={<MesProgres />} />
              <Route path="/devoirs" element={<Devoirs />} />
              <Route path="/madrassa" element={<MadrassaPage />} />
              <Route path="/madrassa/admin" element={<Navigate to="/admin/madrassas" replace />} />
              <Route path="/admin/madrassas" element={<AdminMadrassasPage />} />
              <Route path="/admin/madrassas/:id" element={<AdminMadrassaDetailPage />} />
              <Route path="/admin/madrassa-requests" element={<AdminMadrassaRequestsPage />} />
              <Route path="/madrassa-admin/teacher-invitations" element={<MadrassaTeacherInvitationsPage />} />
              <Route path="/parent" element={<ParentDashboardPage />} />
              <Route path="/parent-link/:token" element={<ParentLinkConfirm />} />
              <Route path="/teacher/recitations/:assignmentId" element={<TeacherRecitationsReviewPage />} />
              <Route path="/teacher/recitations" element={<Navigate to="/devoirs?tab=sent" replace />} />
              <Route path="/teacher/assignments/new" element={<Navigate to="/devoirs?tab=create" replace />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
              <Route path="/teacher/classes" element={<TeacherClassesPage />} />
              <Route path="/teacher/classes/:classId" element={<TeacherClassDetailPage />} />
              {/* Legacy alias kept for the existing console page (email logs etc.) */}
              <Route path="/admin/madrassas/legacy/console" element={<MadrassaAdminPage />} />
              <Route path="/cours/lecture" element={<ApprendreALire />} />
              <Route path="/cours/lecture/upload" element={<LectureAudioUpload />} />
              <Route path="/cours/tajwid" element={<TajwidIndex />} />
              <Route path="/cours/tajwid/leaderboard" element={<TajwidLeaderboard />} />
              <Route path="/cours/tajwid/:rule" element={<TajwidLesson />} />
              {/* Backwards-compatible aliases for the previous /tajwid URLs */}
              <Route path="/tajwid" element={<TajwidIndex />} />
              <Route path="/tajwid/:rule" element={<TajwidLesson />} />
            </Routes>
          </Layout>
          <MiniPlayer />
          <AudioDownloadPrompt />
          <Toaster />
              </div>
        </BrowserRouter>
        </AssignmentBadgesProvider>
        </AudioProvider>
      </PreferencesProvider>
    </AuthProvider>
  );
}

export default App;
